// The status-machine registry (modules-and-api.md §4.3): every status machine is a declared table, not a set of `if`s.
// A module's transition service is the only writer of a status column; it asks the machine, and an unlisted move is
// `ILLEGAL_TRANSITION` carrying the allowed set. Sprint 2 registers `patient` (BR-11) and `appointment` (BR-32); Sprint 4
// `serial`, `stock_take`, `stock_transfer`, `stock_adjustment`, `goods_receipt` and `import`; later sprints add theirs here.
// The status enums live here so every contract that names a status shares one source.
import { ProblemError } from "./problems";

export const PATIENT_STATUSES = ["Active", "Dormant", "Prohibited", "Archived"] as const;
export type PatientStatus = (typeof PATIENT_STATUSES)[number];

/**
 * BR-32 / INT-80, spelled as identifiers (`InProgress`, `NoShow`) — the display forms "In progress" and "No-show" are
 * `APPOINTMENT_STATUS_LABELS` in appointments.ts. A reschedule creates a new appointment; the old one becomes `Rescheduled`.
 */
export const APPOINTMENT_STATUSES = ["Scheduled", "Confirmed", "Arrived", "InProgress", "Completed", "Cancelled", "NoShow", "Rescheduled"] as const;
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number];

export interface StatusMachine<S extends string> {
  readonly name: string;
  readonly states: readonly S[];
  readonly transitions: Readonly<Record<S, readonly S[]>>;
  /** States with no way out. */
  readonly terminal: readonly S[];
  allowedFrom(from: S): readonly S[];
  canTransition(from: S, to: S): boolean;
  /** Throws `ILLEGAL_TRANSITION` with `meta: { machine, from, to, allowed }` for an unlisted move; a no-op move (from = to) is also refused. */
  assertTransition(from: S, to: S): void;
}

/** Declares a machine; refuses at definition time a transition to a state the table does not know. */
export function defineMachine<S extends string>(name: string, transitions: Record<S, readonly S[]>): StatusMachine<S> {
  const states = Object.keys(transitions) as S[];
  for (const from of states) {
    for (const to of transitions[from]) {
      if (!states.includes(to)) throw new Error(`status machine "${name}": ${from} → ${String(to)} names an unknown state`);
    }
  }
  const terminal = states.filter((s) => transitions[s].length === 0);
  const allowedFrom = (from: S): readonly S[] => transitions[from] ?? [];
  const canTransition = (from: S, to: S): boolean => allowedFrom(from).includes(to);
  return {
    name,
    states,
    transitions,
    terminal,
    allowedFrom,
    canTransition,
    assertTransition(from, to) {
      if (canTransition(from, to)) return;
      throw new ProblemError("ILLEGAL_TRANSITION", {
        detail: `${name}: ${from} → ${to} is not allowed`,
        meta: { machine: name, from, to, allowed: [...allowedFrom(from)] },
      });
    },
  };
}

/**
 * BR-11: Active ↔ Dormant; Active/Dormant → Prohibited; any → Archived; Prohibited/Archived → Active. Every transition
 * needs a reason except into Active, writes `patient_status_history`, and `Prohibited` is a step-up transition (TEN-48).
 */
export const PATIENT_STATUS_MACHINE: StatusMachine<PatientStatus> = defineMachine<PatientStatus>("patient", {
  Active: ["Dormant", "Prohibited", "Archived"],
  Dormant: ["Active", "Prohibited", "Archived"],
  Prohibited: ["Active", "Archived"],
  Archived: ["Active"],
});

/** BR-11: the transitions only an Owner or Admin may make — into or out of Prohibited and Archived. Active ↔ Dormant is Receptionist+. */
export function patientTransitionNeedsAdmin(from: PatientStatus, to: PatientStatus): boolean {
  return to === "Prohibited" || to === "Archived" || from === "Prohibited" || from === "Archived";
}
/** TEN-48: setting Prohibited is a Control PIN step-up (`PatientProhibit`). */
export const PATIENT_STATUSES_REQUIRING_STEP_UP: readonly PatientStatus[] = ["Prohibited"];
/** BR-11 / PAT-53: a reason is mandatory into every restricted state. */
export const PATIENT_STATUSES_REQUIRING_REASON: readonly PatientStatus[] = ["Dormant", "Prohibited", "Archived"];

/**
 * BR-32 / BR-APPOINTMENTS-002: Scheduled → Confirmed → Arrived → InProgress → Completed; `cancel` from any of the four live
 * states; `no-show` from Scheduled or Confirmed only (an arrived patient cannot be a no-show); `Rescheduled` is set only by a
 * Bookings pull, and never on a Completed, Cancelled or NoShow appointment (BR-APPOINTMENTS-004). The four terminal states
 * are Completed, Cancelled, NoShow and Rescheduled.
 */
export const APPOINTMENT_STATUS_MACHINE: StatusMachine<AppointmentStatus> = defineMachine<AppointmentStatus>("appointment", {
  Scheduled: ["Confirmed", "Arrived", "Cancelled", "NoShow", "Rescheduled"],
  Confirmed: ["Arrived", "Cancelled", "NoShow", "Rescheduled"],
  Arrived: ["InProgress", "Cancelled", "Rescheduled"],
  InProgress: ["Completed", "Cancelled", "Rescheduled"],
  Completed: [],
  Cancelled: [],
  NoShow: [],
  Rescheduled: [],
});

// --- Sprint 4: serials, stock takes, transfers, adjustments, goods receipts and imports -------------------------------------

/**
 * BR-INVENTORY-011: a serial's statuses, spelled as stored; the screens' words are Available, Reserved, At lab, Sold,
 * In transit, Damaged, Written off and Returned (`SERIAL_STATUS_LABELS` in inventory.ts).
 */
export const SERIAL_STATUSES = ["Available", "Reserved", "AtLab", "Sold", "InTransit", "Damaged", "WrittenOff", "Returned"] as const;
export type SerialStatus = (typeof SERIAL_STATUSES)[number];

/**
 * BR-INVENTORY-011 (R-12, §4.3): Sprint 4 performs a new serial's arrival as Available (no move), Available → WrittenOff (a
 * write-off, or missing at a finalised count; terminal), Available → Damaged (quarantine), Available → InTransit (Send) and
 * InTransit → Available (received at the other branch). Proposed for Sprint 4 where R-12 leaves a status without an exit
 * (UC-INVENTORY-006 and -011 open questions; Khaalid to confirm): InTransit → Available at the sender (a sent transfer
 * cancelled), Damaged → WrittenOff and Damaged → Available. The seams of later sprints: Available ↔ Reserved (Sprint 5),
 * Reserved → AtLab → Reserved and AtLab → Sold (Sprint 7), Reserved → Sold (Sprint 6), Sold → Returned → Available or
 * Damaged (Sprint 6). Voiding a serial recorded in error is not a status (BR-INVENTORY-015).
 */
export const SERIAL_STATUS_MACHINE: StatusMachine<SerialStatus> = defineMachine<SerialStatus>("serial", {
  Available: ["Reserved", "Damaged", "WrittenOff", "InTransit"],
  Reserved: ["Available", "AtLab", "Sold"],
  AtLab: ["Reserved", "Sold"],
  Sold: ["Returned"],
  InTransit: ["Available"],
  Damaged: ["WrittenOff", "Available"],
  WrittenOff: [],
  Returned: ["Available", "Damaged"],
});

/**
 * BR-INVENTORY-016, BR-INVENTORY-019: a count is `Open` (shown In progress) until it is `Finalised` or `Abandoned`; Proposed,
 * `Posting` (shown "Posting…") while a count too large for one unit of work posts in chunks (UC-INVENTORY-010 A4,
 * BR-INVENTORY-027), which leads only to Finalised. The schema draft's Counting and Review are not used (UC-INVENTORY-007).
 */
export const STOCK_TAKE_STATUSES = ["Open", "Posting", "Finalised", "Abandoned"] as const;
export type StockTakeStatus = (typeof STOCK_TAKE_STATUSES)[number];
export const STOCK_TAKE_STATUS_MACHINE: StatusMachine<StockTakeStatus> = defineMachine<StockTakeStatus>("stock_take", {
  Open: ["Posting", "Finalised", "Abandoned"],
  Posting: ["Finalised"],
  Finalised: [],
  Abandoned: [],
});

/**
 * BR-INVENTORY-021 (UC-INVENTORY-011, -012): a transfer is saved as a `Draft`, `Sent` (shown In transit), `PartiallyReceived`
 * or `Received`, or `Cancelled` while a draft. Proposed (UC-INVENTORY-011 A4, A5): a Sent transfer may be cancelled before
 * anything is received; cancelling the rest of a partly received one returns those units to the sender and reads Received.
 */
export const STOCK_TRANSFER_STATUSES = ["Draft", "Sent", "PartiallyReceived", "Received", "Cancelled"] as const;
export type StockTransferStatus = (typeof STOCK_TRANSFER_STATUSES)[number];
export const STOCK_TRANSFER_STATUS_MACHINE: StatusMachine<StockTransferStatus> = defineMachine<StockTransferStatus>("stock_transfer", {
  Draft: ["Sent", "Cancelled"],
  Sent: ["PartiallyReceived", "Received", "Cancelled"],
  PartiallyReceived: ["Received"],
  Received: [],
  Cancelled: [],
});

/**
 * BR-INVENTORY-013: an adjustment posts when it is saved and is never changed — a later adjustment corrects it — so its one
 * status is `Posted` (TC-INVENTORY-027); the schema draft's Draft and Cancelled are not used (UC-INVENTORY-006).
 */
export const STOCK_ADJUSTMENT_STATUSES = ["Posted"] as const;
export type StockAdjustmentStatus = (typeof STOCK_ADJUSTMENT_STATUSES)[number];
export const STOCK_ADJUSTMENT_STATUS_MACHINE: StatusMachine<StockAdjustmentStatus> = defineMachine<StockAdjustmentStatus>("stock_adjustment", {
  Posted: [],
});

/**
 * BR-PURCHASING-014 (W9): a goods receipt is posted once and never edited or deleted — `Posted` (TC-PURCHASING-015). Sprint 7's
 * reversing receipt (LAB-55) adds its own status and move here.
 */
export const GOODS_RECEIPT_STATUSES = ["Posted"] as const;
export type GoodsReceiptStatus = (typeof GOODS_RECEIPT_STATUSES)[number];
export const GOODS_RECEIPT_STATUS_MACHINE: StatusMachine<GoodsReceiptStatus> = defineMachine<GoodsReceiptStatus>("goods_receipt", {
  Posted: [],
});

/**
 * BR-IMPORTS-004, BR-IMPORTS-020 (W8): Uploaded → Checking → Checked → Importing → Imported. A check that cannot finish ends
 * Failed and may be checked again or sent back to Uploaded; a Checked import goes back to Checking (checked again) or to
 * Uploaded (its mapping, scope, markup or discount changed); a run that cannot finish ends Failed and may be resumed
 * (Importing) or discarded (Discarded, kept with what its run wrote withdrawn). Discarding an import whose Apply never started
 * removes it and is no move. Imported and Discarded are final in Sprint 4 (rollback is Sprint 11). Which of Failed's moves
 * applies follows its failed phase (`IMPORT_FAILED_PHASES`), checked by the imports module.
 */
export const IMPORT_JOB_STATUSES = ["Uploaded", "Checking", "Checked", "Importing", "Imported", "Failed", "Discarded"] as const;
export type ImportJobStatus = (typeof IMPORT_JOB_STATUSES)[number];
export const IMPORT_JOB_STATUS_MACHINE: StatusMachine<ImportJobStatus> = defineMachine<ImportJobStatus>("import", {
  Uploaded: ["Checking"],
  Checking: ["Checked", "Failed"],
  Checked: ["Checking", "Uploaded", "Importing"],
  Importing: ["Imported", "Failed"],
  Imported: [],
  Failed: ["Checking", "Uploaded", "Importing", "Discarded"],
  Discarded: [],
});

/** The registry §4.3 describes, keyed by the machine name (the table whose status column it owns; `import` is the import job's). */
export const STATUS_MACHINES = {
  patient: PATIENT_STATUS_MACHINE,
  appointment: APPOINTMENT_STATUS_MACHINE,
  serial: SERIAL_STATUS_MACHINE,
  stock_take: STOCK_TAKE_STATUS_MACHINE,
  stock_transfer: STOCK_TRANSFER_STATUS_MACHINE,
  stock_adjustment: STOCK_ADJUSTMENT_STATUS_MACHINE,
  goods_receipt: GOODS_RECEIPT_STATUS_MACHINE,
  import: IMPORT_JOB_STATUS_MACHINE,
} as const;
export type StatusMachineName = keyof typeof STATUS_MACHINES;
